import { useStorageService } from '~/composables/services/useStorageService';
import type {
  IBannerTagResponse,
  IInfoResponse,
  IMarketingOptInResponse,
  IMarketingSessionAddResponse,
  ITokensResponse,
} from '~/interfaces/dto/marketing/response';
import type {
  IMarketingOptInRequest,
  IPlatformPublishMarketingEventRequest,
  IMarketingSessionAddRequest,
  ISharedMarketingSessionDetails,
} from '~/interfaces/dto/marketing/request';
import { globalKeys } from 'assets/data/global-keys';
import { useAuthStore } from '~/stores/auth.store';
import { useSiteStore } from '~/stores/site.store';
import { type BannersResponse } from '~/interfaces/components/banner';
import useIdentifier from '~/composables/useIdentifier';
import type {
  INotificationsRequest,
  INotificationsResponse,
  IUpdateNotificationsStatusRequest,
} from '~/interfaces/notifications/notifications';
import {
  marketingSessionPlatformIds,
  marketingSessionRegionIds,
} from 'assets/data/config';
import { useLocale } from '~/composables/useStates/useLocale';
import { getUserDeviceInfo } from '~/utilities/device-info';
import { getId, getInstallations } from '@firebase/installations';

export const useMarketingService = () => {
  const $storageService = useStorageService();
  const { $fetch, $dayjs } = useNuxtApp();
  const auth = useAuthStore();
  const siteStore = useSiteStore();
  const route = useRoute();

  const publishPlatformMarketingEvent = async ({
    type,
    data,
    username,
    jwtToken,
    id,
  }: Partial<IPlatformPublishMarketingEventRequest> & {
    jwtToken: string;
    id: string;
  }): Promise<boolean> => {
    const isMobile = useViewport().isLessThan('tablet');
    const platformName = isMobile ? 'WebMobile' : 'WebDesktop';
    const masterToken = String(
      $storageService.getCookie({
        key: globalKeys.marketingKeys.masterToken,
      }),
    );
    const sessionToken = String(
      $storageService.getCookie({
        key: globalKeys.marketingKeys.sessionToken,
      }),
    );
    const sessionTrackingToken = getUserDeviceInfo()?.hash;
    const bannerTag = String(
      $storageService.getCookie({ key: globalKeys.marketingKeys.bannerTag }) ||
        String(route.query?.btag),
    );
    const latestRefererBannerTag = String(
      $storageService.getCookie({
        key: globalKeys.marketingKeys.referrerBannerTag,
      }),
    );
    const queryParams = String(
      $storageService.getCookie({
        key: globalKeys.marketingKeys.originalQueryString,
      }),
    );
    const tradeDeskExternalRef = String(
      $storageService.getCookie({
        key: globalKeys.tradeDeskKeys.tradeDeskExternalRef,
      }),
    );
    const tokenInCookie = String(
      $storageService.getCookie({ key: globalKeys.authKeys.accessToken }),
    );
    const authorization = !!jwtToken
      ? `Bearer ${jwtToken}`
      : `Bearer ${tokenInCookie}`;

    const installations = getInstallations();
    const firebaseInstallationId = await getId(installations);

    const body: IPlatformPublishMarketingEventRequest = {
      type,
      countryCode: siteStore.getRegionCode,
      data: {
        player_id: id,
        banner_tag: bannerTag,
        session_token: sessionToken,
        master_token: masterToken,
        accountId: id,
        eventDate: $dayjs().format('YYYY-MM-DD'),
        ...data,
      },
      sessionInformation: {
        platformName,
        dataFree: false,
        ...(!!sessionTrackingToken && { sessionTrackingToken }),
        isSynapse: false,
        ...(!!bannerTag && { bannerTag }),
        ...(!!queryParams && { queryParams }),
        ...(!!latestRefererBannerTag && { latestRefererBannerTag }),
        ...(!!masterToken && { masterToken }),
        ...(!!sessionToken && { sessionToken }),
        ...(!!tradeDeskExternalRef && { tradeDeskExternalRef }),
        firebaseInstallationId: firebaseInstallationId || '',
        firebaseInstanceId: firebaseInstallationId || '',
      },
      username,
    };

    try {
      const didSend = await $fetch<boolean>(
        'api/v1/Marketing/PlatformEventPublish',
        {
          base: 'base',
          body,
          method: 'POST',
          headers: {
            authorization,
          },
        },
      );
      return !!didSend;
    } catch (error) {
      console.error(error);
    }
  };

  const getSharedMarketingSessionDetails =
    (): ISharedMarketingSessionDetails => {
      const { query } = route;

      const { deviceInfo, hash } =
        $storageService.getSessionStorage<IInfoResponse>({
          key: globalKeys.siteConfigKeys.deviceInfo,
        });

      const appType =
        (query?.appType as string) ||
        (deviceInfo?.IsMobile || deviceInfo?.IsTablet ? 'mobile' : 'desktop');

      const platformId = marketingSessionPlatformIds[appType.toLowerCase()];
      const regionId = marketingSessionRegionIds[siteStore.getRegionCode];
      const accountId = $storageService.getCookie<string>({
        key: globalKeys.authKeys.userId,
      });
      const datafree = false;

      return {
        platformID: platformId,
        platformId,
        regionID: regionId,
        regionId,
        vendorAccountID: accountId,
        accountId,
        dataFree: datafree,
        datafree,
        sessionTrackingToken: hash,
      };
    };

  const fetchBannerTags = async ({
    referrer,
  }: {
    referrer: string;
  }): Promise<IBannerTagResponse> => {
    const currentBtag =
      $storageService.getCookie({ key: globalKeys.marketingKeys.bannerTag }) ||
      route.query?.btag;
    const currentReferrerBtag = $storageService.getCookie({
      key: globalKeys.marketingKeys.referrerBannerTag,
    });

    return await $fetch<IBannerTagResponse>(`api/v1/origin/bannertag`, {
      base: 'info',
      useOrigin: true,
      query: {
        'request.referringUrl': referrer,
      },
      onResponse({ response }) {
        if (!currentBtag)
          $storageService.setCookie({
            key: globalKeys.marketingKeys.bannerTag,
            data: response._data?.BannerTagString,
            expiry: 0,
          });
        if (!currentReferrerBtag)
          $storageService.setCookie({
            key: globalKeys.marketingKeys.referrerBannerTag,
            data: response._data?.BannerTagString,
            expiry: 0,
          });
      },
      onResponseError({ response }) {
        console.error(response);
      },
      onRequestError({ response }) {
        console.error(response);
      },
    });
  };
  const fetchOriginTokens = async (): Promise<ITokensResponse> => {
    const masterToken = $storageService.getCookie({
      key: globalKeys.marketingKeys.masterToken,
    });
    const sessionToken = $storageService.getCookie({
      key: globalKeys.marketingKeys.sessionToken,
    });
    return await $fetch<ITokensResponse>(`api/v1/origin/tokens`, {
      base: 'info',
      onResponse({ response }) {
        if (!masterToken)
          $storageService.setCookie({
            key: globalKeys.marketingKeys.masterToken,
            data: response._data?.MasterToken,
            expiry: 7,
          });
        if (!sessionToken)
          $storageService.setCookie({
            key: globalKeys.marketingKeys.sessionToken,
            data: response._data?.MasterToken,
            expiry: 0,
          });
      },
      onResponseError({ response }) {
        console.error(response);
      },
      onRequestError({ response }) {
        console.error(response);
      },
    });
  };
  const fetchDeviceInfo = async (): Promise<IInfoResponse> =>
    $fetch(`api/v1/requestInfo`, {
      base: 'info',
      onResponse({ response }) {
        $storageService.setSessionStorage({
          key: globalKeys.siteConfigKeys.deviceInfo,
          data: response._data,
        });

        return response._data;
      },
      onResponseError({ response }) {
        console.error(response);
      },
      onRequestError({ response }) {
        console.error(response);
      },
    });
  const setMarketingSession = async (
    session: IMarketingSessionAddRequest,
  ): Promise<IMarketingSessionAddResponse> =>
    await $fetch<IMarketingSessionAddResponse>(
      `api/v1/marketing/addmarketingsession`,
      {
        base: 'base',
        method: 'post',
        body: { ...session, ...getSharedMarketingSessionDetails() },
        onResponse() {
          $storageService.setSessionStorage({
            key: globalKeys.marketingKeys.marketingSession,
            data: String(true),
          });
        },
        onResponseError({ response }) {
          console.error(response);
        },
        onRequestError({ response }) {
          console.error(response);
        },
      },
    );
  const setSessionData = async () =>
    await $fetch<IMarketingSessionAddResponse>(`api/v1/Marketing/sessiondata`, {
      base: 'base',
      method: 'put',
      useAuthentication: true,
      body: getSharedMarketingSessionDetails(),
      onResponseError({ response }) {
        console.error(response);
      },
      onRequestError({ response }) {
        console.error(response);
      },
    });
  const setMarketingOptIn = async (
    session: IMarketingOptInRequest,
  ): Promise<IMarketingOptInResponse> =>
    await $fetch<IMarketingOptInResponse>(
      `api/v1/marketing/insertmarketingoptin`,
      {
        method: 'post',
        base: 'base',
        body: session,
        onResponseError({ response }) {
          console.error(response);
        },
        onRequestError({ response }) {
          console.error(response);
        },
      },
    );
  const addLiveChatNote = async (interactionId: string) => {
    const request = {
      message: 'Platform: Jackpotcity; AppType: Web;',
      message_type: 'note',
    };

    return await $fetch(`api/v1/LiveChat/AddLiveChatNote`, {
      base: 'base',
      method: 'post',
      query: { interactionId },
      body: request,
    });
  };
  // Banners
  const fetchBanners = async (vertical: string): Promise<BannersResponse[]> =>
    await $fetch<BannersResponse[]>('All/Active', {
      base: auth.isLoggedIn ? 'base' : 'baseC',
      query: {
        vertical,
        channelCode: 'WebDesktop',
        isLoggedInBanner: auth.isLoggedIn,
        'api-version': '1',
      },
      useCache: !auth.isLoggedIn,
      useAuthentication: auth.isLoggedIn,
      usePlayerId: auth.isLoggedIn,
      useRegionCode: true,
      onResponse({ response }) {
        if (!!response?._data?.length)
          response._data = response?._data.map((d, index) => ({
            ...d,
            id: useIdentifier(),
            index,
          }));
      },
    });

  const fetchNotifications = async (
    request: INotificationsRequest,
  ): Promise<INotificationsResponse> => {
    const defaults = {
      skip: 0,
      limit: 20,
      startDate: $dayjs()
        .subtract(6, 'month')
        .startOf('day')
        .utc(true)
        .format(),
      endDate: $dayjs().endOf('day').utc(true).format(),
    };
    const merged = { ...defaults, ...(!!request && request) };
    const { skip, limit, startDate, endDate } = merged;
    const { getLocale } = useLocaleUtils();
    const cultureCode = getLocale();
    try {
      return await $fetch('api/v2/Notification', {
        base: 'base',
        useAuthentication: true,
        cacheBustKey: 'notifications',
        query: {
          ...(!!startDate && { startDate }),
          ...(!!endDate && { endDate }),
          ...((!!skip || skip === 0) && { skip }),
          ...(!!limit && { limit }),
          cultureCode,
        },
      });
    } catch (e) {
      console.error(e);
    }
  };

  const updateNotificationStatusByIds = async ({
    ids,
    updateType,
    isRead,
  }: IUpdateNotificationsStatusRequest) => {
    try {
      return await $fetch(`api/v2/Notification/${updateType}`, {
        base: 'base',
        method: 'post',
        useAuthentication: true,
        body: ids,
        cacheBustKey: 'notifications',
        ...(updateType === 'Status' && {
          query: {
            isRead,
          },
        }),
      });
    } catch (e) {
      console.error(e);
    }
  };

  const subscribeToTopic = async (currentToken: string) => {
    const locale = useLocale();
    try {
      await $fetch(`api/v1/Notification/Firebase/Subscribe/Topic`, {
        base: 'base',
        method: 'POST',
        useAuthentication: true,
        headers: {
          'x-pushtoken': currentToken,
          'Content-Type': 'application/json',
        },
        query: { cultureCode: locale.value.locale },
        body: null,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const fetchToasts = async (): Promise<void> => {
    const site = useSiteStore();
    const auth = useAuthStore();
    const { $toastService } = useNuxtApp();
    try {
      const requestUrl = `api/v1/Toast/${site.getRegionCode}`;
      await $fetch(requestUrl, {
        useAuthentication: auth.isLoggedIn,
        base: auth.isLoggedIn ? 'base' : 'baseC',
        onResponse({ response }) {
          const data = Array.isArray(response._data) ? response._data : [];
          $toastService.addToast(data);
        },
      });
    } catch (e) {
      console.error(e);
    }
  };

  const registerPushToken = async (currentToken: string) => {
    const { deviceInfo } = getUserDeviceInfo();
    const deviceId =
      $storageService.getLocalStorage<string>({
        key: globalKeys.siteConfigKeys.deviceIdentifier,
      }) ||
      deviceInfo?.DeviceId ||
      'unknown-device';
    const deviceType = !!deviceInfo?.IsMobile ? 'WebMobile' : 'WebDesktop';
    const deviceLabel = !!deviceInfo?.DeviceOsName
      ? deviceInfo?.DeviceOsName
      : window.navigator.platform;
    const body = {
      ...(!!deviceId && {
        deviceId: deviceId,
      }),
      ...(!!deviceLabel && { deviceLabel }),
      ...(!!deviceType && { deviceType }),
    };
    try {
      await $fetch(`api/v2/Notification/Register`, {
        base: 'base',
        method: 'POST',
        useAuthentication: true,
        headers: {
          'x-pushtoken': currentToken,
          'Content-Type': 'application/json',
        },
        body,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const deregisterPushToken = async (currentToken: string) => {
    try {
      await $fetch(`api/v2/Notification/Deregister`, {
        base: 'base',
        method: 'POST',
        useAuthentication: true,
        headers: {
          'x-pushtoken': currentToken,
          'Content-Type': 'application/json',
        },
        body: null,
      });
    } catch (e) {
      console.error(e);
    }
  };

  return {
    addLiveChatNote,
    registerPushToken,
    deregisterPushToken,
    fetchDeviceInfo,
    fetchOriginTokens,
    fetchBanners,
    fetchBannerTags,
    fetchNotifications,
    fetchToasts,
    publishPlatformMarketingEvent,
    setMarketingOptIn,
    setMarketingSession,
    setSessionData,
    subscribeToTopic,
    updateNotificationStatusByIds,
  };
};
